import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import Tenants from "@/pages/Tenants";
import Owners from "@/pages/Owners";
import Agreements from "@/pages/Agreements";
import Invoices from "@/pages/Invoices";
import Payments from "@/pages/Payments";
import Outstanding from "@/pages/Outstanding";
import Expenses from "@/pages/Expenses";
import Deposits from "@/pages/Deposits";
import Banking from "@/pages/Banking";
import Reports from "@/pages/Reports";
import VacantUnits from "@/pages/VacantUnits";
import TenantSummary from "@/pages/TenantSummary";
import CalendarPage from "@/pages/CalendarPage";
import NotificationsPage from "@/pages/Notifications";
import Documents from "@/pages/Documents";
import SettingsPage from "@/pages/Settings";
import Users from "@/pages/Users";
import AdminPanel from "@/pages/AdminPanel";
import Backup from "@/pages/Backup";
import TenantPortalLogin from "@/pages/TenantPortalLogin";
import TenantPortalDashboard from "@/pages/TenantPortalDashboard";
import TenantApprovals from "@/pages/TenantApprovals";
import { Loader2, ShieldAlert } from "lucide-react";

function AccessRestricted() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="access-restricted">
      <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mb-5"><ShieldAlert className="w-8 h-8 text-rose-500" /></div>
      <h1 className="font-heading text-2xl font-bold text-slate-800">Access restricted</h1>
      <p className="text-slate-500 mt-2 max-w-md text-sm">You don't have permission to view this page. Please contact your business administrator if you believe this is a mistake.</p>
      <a href="/" className="mt-6 inline-flex items-center px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors" data-testid="access-restricted-home">Back to Dashboard</a>
    </div>
  );
}

function Protected({ children, perm, superOnly }) {
  const { user, activeId, can } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!activeId) return <Onboarding />;
  const allowed = superOnly ? !!user.is_super_admin : (!perm || can(perm));
  return <AppShell>{allowed ? children : <AccessRestricted />}</AppShell>;
}
function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function GoogleCallback() {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return; done.current = true;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    (async () => {
      try {
        await loginWithGoogle(sid);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Google sign-in failed. Please try again.");
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/login", { replace: true });
      }
    })();
  }, []); // eslint-disable-line
  return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
}

function RootRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <GoogleCallback />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/portal/login" element={<TenantPortalLogin />} />
      <Route path="/portal" element={<TenantPortalDashboard />} />
      <Route path="/portal/*" element={<TenantPortalDashboard />} />
      <Route path="/" element={<Protected perm="dashboard.view"><Dashboard /></Protected>} />
      <Route path="/properties" element={<Protected perm="properties.view"><Properties /></Protected>} />
      <Route path="/tenants" element={<Protected perm="tenants.view"><Tenants /></Protected>} />
      <Route path="/owners" element={<Protected perm="owners.view"><Owners /></Protected>} />
      <Route path="/agreements" element={<Protected perm="agreements.view"><Agreements /></Protected>} />
      <Route path="/invoices" element={<Protected perm="invoices.view"><Invoices /></Protected>} />
      <Route path="/payments" element={<Protected perm="payments.view"><Payments /></Protected>} />
      <Route path="/tenant-approvals" element={<Protected perm="approvals.view"><TenantApprovals /></Protected>} />
      <Route path="/outstanding" element={<Protected perm="outstanding.view"><Outstanding /></Protected>} />
      <Route path="/expenses" element={<Protected perm="expenses.view"><Expenses /></Protected>} />
      <Route path="/deposits" element={<Protected perm="deposits.view"><Deposits /></Protected>} />
      <Route path="/banking" element={<Protected perm="banking.view"><Banking /></Protected>} />
      <Route path="/reports" element={<Protected perm="reports.view"><Reports /></Protected>} />
      <Route path="/vacant-units" element={<Protected perm="reports.view"><VacantUnits /></Protected>} />
      <Route path="/tenant-summary" element={<Protected perm="tenant_sheet.view"><TenantSummary /></Protected>} />
      <Route path="/calendar" element={<Protected perm="calendar.view"><CalendarPage /></Protected>} />
      <Route path="/notifications" element={<Protected perm="notifications.view"><NotificationsPage /></Protected>} />
      <Route path="/documents" element={<Protected perm="documents.view"><Documents /></Protected>} />
      <Route path="/users" element={<Protected perm="users.view"><Users /></Protected>} />
      <Route path="/backup" element={<Protected perm="backup.view"><Backup /></Protected>} />
      <Route path="/admin" element={<Protected superOnly><AdminPanel /></Protected>} />
      <Route path="/settings" element={<Protected perm="settings.view"><SettingsPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RootRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
export default App;
